import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import {isFileConfigVariable} from 'analysis-tool/templates/AnalysisTemplateVariableUtils';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ConnectionFilesTable, {
  getFiltersFromConnectionIds,
  useConnectionIdsAndFiltersState,
} from 'instruments/connection-files/ConnectionFilesTable';
import {
  GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode as AnalysisTemplateNode,
  GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode_configVariables_AnalysisTemplateVariableNode as AnalysisTemplateConfigVariableNode,
} from 'results/analysis-integration/__generated/GetAnalysisTemplateForAssayRun';
import SearchInput from 'search/inputs/SearchInput';
import {Filter, FilterKey} from 'search/SearchConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import Stepper, {StepperStep} from 'ui-library/stepper/Stepper';

import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';

export interface ConfigureInputFileFiltersModalProps {
  analysisTemplate: AnalysisTemplateNode;
  initialDefaultFiltersMapping: {[configVariableId: string]: Array<Filter>} | null;
  onSaveDefaultFiltersMapping: (
    newDefaultFiltersMapping: {[configVariableId: string]: Array<Filter>} | null
  ) => void;
  onRequestClose: () => void;
}

export default function ConfigureInputFileFiltersModal(
  props: ConfigureInputFileFiltersModalProps
): JSX.Element {
  const {analysisTemplate, initialDefaultFiltersMapping, onSaveDefaultFiltersMapping, onRequestClose} = props;
  // This will be used if no filters are persisted for a given selectedVariable, or if selectedVariable is null
  const DEFAULT_CONNECTION_FILES_TABLE_FILTERS = useMemo(() => getFiltersFromConnectionIds([]), []);

  // Which file variable's filters is currently being configured
  const [selectedVariable, _setSelectedVariable] = useState<AnalysisTemplateConfigVariableNode | null>(null);
  // The filter configuration that will be persisted on save. This is updated by onSetSelectedConnectionIds
  // when the user selects or deselects connections to filter by.
  const [draftDefaultFiltersMapping, setDraftDefaultFiltersMapping] = useState<{
    [configVariableId: string]: Array<Filter>;
  } | null>(initialDefaultFiltersMapping);
  // State management for ConnectionFilesTable
  const {
    // Use onSetSelectedConnectionIds instead of _setSelectedConnectionIds
    connectionIdsState: [selectedConnectionIds, _setSelectedConnectionIds],
    // Use onSetFilters instead of _setFilters
    filtersState: [filters, _setFilters],
  } = useConnectionIdsAndFiltersState({
    // selectedVariable is null when the component first renders, so we don't have filters information yet
    initialFilters: DEFAULT_CONNECTION_FILES_TABLE_FILTERS,
    initialConnectionIds: null,
  });
  const [connectionFiles, setConnectionFiles] = useState<Array<FileListingGraphQL>>([]);

  // When filters is updated, then also update draftDefaultFiltersMapping with newFilters
  function onSetFilters(newFilters: Array<Filter>): void {
    _setFilters(newFilters);

    const variableKeyToUpdate = selectedVariable?.id;
    assert(variableKeyToUpdate != null, 'User was able to configure filters for a null config variable.');

    // Overwrite the filters assigned to selectedVariable
    setDraftDefaultFiltersMapping({
      ...draftDefaultFiltersMapping,
      [variableKeyToUpdate]: newFilters,
    });
  }

  // Translate connectionIds to a filter update, then call onSetFilters. This has the effect of:
  // - updating filters
  // - updating selectedConnectionIds (because it's connected to filters via useConnectionIdsAndFiltersState)
  // - updating draftDefaultFiltersMapping (because onSetFilters is called)
  function onSetSelectedConnectionIds(newSelectedConnectionIds: Array<string>): void {
    const newFilters = filters.map((filter_) =>
      filter_.key === FilterKey.LINEAGE_PARENT_API_IDENTIFIER
        ? {...filter_, value: newSelectedConnectionIds}
        : filter_
    );
    onSetFilters(newFilters);
  }

  // When selectedVariable is updated, then update filtersState to what exists in draftDefaultFiltersMapping
  // This has the effect of:
  // - updating filters
  // - updating selectedConnectionIds (because it's connected to filters via useConnectionIdsAndFiltersState)
  function onSetSelectedVariable(newSelectedVariable: AnalysisTemplateConfigVariableNode | null): void {
    _setSelectedVariable(newSelectedVariable);

    if (
      newSelectedVariable != null &&
      draftDefaultFiltersMapping != null &&
      draftDefaultFiltersMapping.hasOwnProperty(newSelectedVariable.id)
    ) {
      _setFilters(draftDefaultFiltersMapping[newSelectedVariable.id]);
    } else {
      _setFilters(DEFAULT_CONNECTION_FILES_TABLE_FILTERS);
    }
  }

  return (
    <Modal
      large={true}
      enableRedesign={true}
      title="Configure default data source filters"
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onSaveDefaultFiltersMapping(
          draftDefaultFiltersMapping != null && Object.keys(draftDefaultFiltersMapping).length > 0
            ? draftDefaultFiltersMapping
            : null
        );
        onRequestClose();
      }}
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: 'Save',
          submit: true,
          disabled: false,
        },
      ]}
    >
      <Stepper>
        <StepperStep title="Select input file field to configure">
          <SelectDropdown
            displayName={selectedVariable?.name ?? 'Select an input file field...'}
            menu={
              <Menu width={MenuWidth.FULL}>
                {analysisTemplate.configVariables
                  .filter((variable) => isFileConfigVariable(variable))
                  .map((variable) => (
                    <Option
                      key={variable.id}
                      onSelect={() => {
                        onSetSelectedVariable(variable);
                      }}
                    >
                      {variable.name}
                    </Option>
                  ))}
              </Menu>
            }
          />
        </StepperStep>
        {selectedVariable && (
          <StepperStep title="Configure connections filter and preview files">
            <div className={css(styles.connectionTabBody)}>
              <div>
                <FieldLabel label="Connections to filter by" />
                <SearchInput
                  autoFocus={false}
                  types={[ModelTypeConstants.CONNECTION]}
                  selected={selectedConnectionIds}
                  onSelect={(connectionId) => {
                    if (connectionId == null) {
                      return;
                    }
                    onSetSelectedConnectionIds([...selectedConnectionIds, connectionId]);
                  }}
                  onRemove={(connectionId) => {
                    if (connectionId == null) {
                      return;
                    }
                    onSetSelectedConnectionIds(selectedConnectionIds.filter((id) => id !== connectionId));
                  }}
                  placeholder="Search for connections..."
                  isMulti={true}
                />
              </div>
              <div>
                <FieldLabel label="Connection files preview" />
                <ConnectionFilesTable
                  hideCheckboxColumn={true}
                  connectionFiles={connectionFiles}
                  setConnectionFiles={setConnectionFiles}
                  columns={['File name', 'Status', 'Modified at', 'Created at']}
                  filters={filters}
                  setFilters={onSetFilters}
                />
              </div>
            </div>
          </StepperStep>
        )}
      </Stepper>
    </Modal>
  );
}

const styles = StyleSheet.create({
  connectionTabBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
});
